import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  FiRefreshCw,
  FiCheckCircle,
  FiXCircle,
  FiSend,
  FiX,
} from 'react-icons/fi';
import AdminShell from '../../components/AdminShell/AdminShell';
import PageHeader from '../../components/PageHeader/PageHeader';
import axiosClient from '../../api/axiosClient';
import panelStyles from '../../styles/adminPanels.module.css';
import styles from '../AdminPortalInvestments/AdminPortalInvestments.module.css';

type ClaimStatus =
  | 'pending'
  | 'approved'
  | 'processing'
  | 'completed'
  | 'rejected'
  | 'failed';

type Action = 'approved' | 'rejected' | 'processing' | 'completed';

interface TokenClaim {
  id: string;
  user_id: string;
  investment_id: string;
  full_name: string;
  email: string;
  portal: 'A' | 'B' | 'C';
  token_amount: string;
  charge_admin_tokens?: string | null;
  charge_tds_tokens?: string | null;
  charge_tokens?: string | null;
  wallet_address: string;
  status: ClaimStatus;
  rejection_reason: string | null;
  failure_reason: string | null;
  transaction_hash: string | null;
  created_at: string;
  reviewed_at: string | null;
  processing_at: string | null;
  completed_at: string | null;
}

interface SelectedAction {
  claim: TokenClaim;
  action: Action;
}

const PAGE_SIZE = 20;

const STATUS_LABELS: Record<ClaimStatus, string> = {
  pending: 'Awaiting review',
  approved: 'Approved',
  processing: 'Processing',
  completed: 'Completed',
  rejected: 'Rejected',
  failed: 'Needs review',
};

const STATUS_COLORS: Record<ClaimStatus, string> = {
  pending: '#b7791f',
  approved: '#1d60f6',
  processing: '#805ad5',
  completed: '#23834b',
  rejected: '#e2534a',
  failed: '#b7791f',
};

const ACTION_LABELS: Record<Action, string> = {
  approved: 'Approve Wallet & Claim',
  rejected: 'Reject Claim',
  processing: 'Mark as Processing',
  completed: 'Confirm Transfer Completed',
};

const ALLOWED_ACTIONS: Record<ClaimStatus, Action[]> = {
  pending: ['approved', 'rejected'],
  approved: ['processing', 'rejected'],
  processing: ['completed'],
  completed: [],
  rejected: [],
  failed: [],
};

function quantity(value: string) {
  const [whole, fraction = ''] = String(value).split('.');
  const formatted = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const decimal = fraction.replace(/0+$/, '');

  return decimal ? `${formatted}.${decimal}` : formatted;
}

function dateLabel(value: string | null) {
  if (!value) return '—';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function errorMessage(error: unknown, fallback: string) {
  const candidate = error as {
    response?: {
      data?: {
        message?: string;
      };
    };
  };

  return candidate.response?.data?.message || fallback;
}

const AdminBctTokenClaims: React.FC = () => {
  const [claims, setClaims] = useState<TokenClaim[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [refresh, setRefresh] = useState(0);

  const [selected, setSelected] = useState<SelectedAction | null>(null);
  const [walletVerified, setWalletVerified] = useState(false);
  const [processingConfirmed, setProcessingConfirmed] = useState(false);
  const [transferConfirmed, setTransferConfirmed] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [transactionHash, setTransactionHash] = useState('');
  const [saving, setSaving] = useState(false);

  const mutationLock = useRef(false);

  useEffect(() => {
    let active = true;

    setLoading(true);
    setLoadError('');

    axiosClient.get('/bct-token/admin/claims', {
      params: {
        limit: PAGE_SIZE,
        offset,
        ...(statusFilter ? { status: statusFilter } : {}),
      },
    })
      .then(({ data }) => {
        if (!Array.isArray(data.claims)) {
          throw new Error('Invalid claim response');
        }

        if (active) {
          setClaims(data.claims);
          setHasMore(data.hasMore === true);
        }
      })
      .catch((error: unknown) => {
        if (active) {
          setLoadError(
            errorMessage(error, 'Could not load BCT100 claims.'),
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [offset, statusFilter, refresh]);

  const reload = () => setRefresh((value) => value + 1);

  const openAction = (claim: TokenClaim, action: Action) => {
    if (
      mutationLock.current
      || loading
      || loadError
      || !(ALLOWED_ACTIONS[claim.status] || []).includes(action)
    ) {
      return;
    }

    setWalletVerified(false);
    setProcessingConfirmed(false);
    setTransferConfirmed(false);
    setRejectionReason('');
    setTransactionHash('');
    setSelected({ claim, action });
  };

  const closeAction = () => {
    if (!mutationLock.current) setSelected(null);
  };

  const submitAction = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selected || mutationLock.current) return;

    const { claim, action } = selected;

    const payload: {
      status: Action;
      walletVerified?: boolean;
      rejectionReason?: string;
      transactionHash?: string;
      transferConfirmed?: boolean;
    } = {
      status: action,
    };

    if (action === 'approved') {
      if (!walletVerified) {
        toast.error('Verify the receiving wallet address first.');
        return;
      }

      payload.walletVerified = true;
    }

    if (action === 'rejected') {
      const reason = rejectionReason.trim();

      if (
        !reason
        || reason.length > 2000
        || /[\u0000-\u001f\u007f]/.test(reason)
      ) {
        toast.error(
          'Enter a rejection reason of up to 2,000 characters in one line.',
        );
        return;
      }

      payload.rejectionReason = reason;
    }

    if (action === 'processing' && !processingConfirmed) {
      toast.error('Confirm that you are taking responsibility for this transfer.');
      return;
    }

    if (action === 'completed') {
      const hash = transactionHash.trim();

      if (
        !hash
        || hash.length > 255
        || /[\s\u0000-\u001f\u007f]/.test(hash)
      ) {
        toast.error('Enter a valid transaction hash without spaces.');
        return;
      }

      if (!transferConfirmed) {
        toast.error('Confirm that the token transfer succeeded.');
        return;
      }

      payload.transactionHash = hash;
      payload.transferConfirmed = true;
    }

    mutationLock.current = true;
    setSaving(true);

    try {
      const { data } = await axiosClient.patch(
        `/bct-token/admin/claims/${claim.id}/status`,
        payload,
      );

      toast.success(data.message || 'Claim updated.');
      setSelected(null);
      reload();
    } catch (error: unknown) {
      toast.error(
        errorMessage(
          error,
          'Could not confirm the update. Check the refreshed claim status before retrying.',
        ),
      );

      setSelected(null);
      reload();
    } finally {
      mutationLock.current = false;
      setSaving(false);
    }
  };

  const submitDisabled = saving
    || !selected
    || (selected.action === 'approved' && !walletVerified)
    || (selected.action === 'processing' && !processingConfirmed)
    || (
      selected.action === 'rejected'
      && !rejectionReason.trim()
    )
    || (
      selected.action === 'completed'
      && (!transactionHash.trim() || !transferConfirmed)
    );

  return (
    <AdminShell>
      <PageHeader
        title="BCT100 Claims"
        subtitle="Review receiving wallets, approve claims and record token transfers."
        action={
          <button
            type="button"
            className={styles.createBtn}
            disabled={loading || saving}
            onClick={reload}
          >
            <FiRefreshCw />
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        }
      />

      <div className={panelStyles.page}>
        <div
          style={{
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <label htmlFor="bct-claim-status">Claim Status</label>

          <select
            id="bct-claim-status"
            className={styles.input}
            style={{ width: 240, margin: 0 }}
            value={statusFilter}
            disabled={saving}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setOffset(0);
            }}
          >
            <option value="">All statuses</option>
            <option value="pending">Awaiting review</option>
            <option value="approved">Approved</option>
            <option value="processing">Processing</option>
            <option value="completed">Completed</option>
            <option value="rejected">Rejected</option>
            <option value="failed">Needs review</option>
          </select>
        </div>

        <p className={styles.hint} style={{ marginBottom: 16 }}>
          Approval confirms the reviewed wallet and claim. Tokens are sent
          separately. Mark the claim as processing before sending, then
          record the successful transaction hash.
        </p>

        <div
          className={panelStyles.tableWrap}
          style={{ overflowX: 'auto' }}
        >
          {loading ? (
            <div className={panelStyles.loading}>
              <span className={panelStyles.spinnerLg} />
            </div>
          ) : loadError ? (
            <div className={panelStyles.empty} role="alert">
              <p>{loadError}</p>
              <button
                type="button"
                className={panelStyles.actionBtn}
                onClick={reload}
              >
                Retry
              </button>
            </div>
          ) : claims.length === 0 ? (
            <div className={panelStyles.empty}>
              No claims on this page.
            </div>
          ) : (
            <table className={panelStyles.table}>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Portal / Tokens</th>
                  <th>Receiving Wallet</th>
                  <th>Status</th>
                  <th>Dates (IST)</th>
                  <th>Transfer / Review</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {claims.map((claim) => (
                  <tr key={claim.id}>
                    <td>
                      <div className={panelStyles.nameCell}>
                        {claim.full_name}
                      </div>
                      <div className={panelStyles.subCell}>
                        {claim.email}
                      </div>
                      <div
                        className={panelStyles.subCell}
                        style={{ overflowWrap: 'anywhere' }}
                      >
                        Claim: {claim.id}
                      </div>
                    </td>

                    <td>
                      <div>Portal {claim.portal}</div>
                      <strong>
                        {quantity(claim.token_amount)} BCT100
                      </strong>
                      {Number(claim.charge_tokens) > 0 && (
                        <div className={panelStyles.subCell}>
                          Admin {quantity(String(claim.charge_admin_tokens || 0))} + TDS {quantity(String(claim.charge_tds_tokens || 0))} · send {quantity(String(Number(claim.token_amount) - Number(claim.charge_tokens)))}
                        </div>
                      )}
                      <div className={panelStyles.subCell}>
                        Investment: {claim.investment_id.slice(0, 8)}
                      </div>
                    </td>

                    <td>
                      <div
                        style={{
                          minWidth: 180,
                          maxWidth: 260,
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {claim.wallet_address}
                      </div>
                    </td>

                    <td>
                      <span
                        className={panelStyles.statusPill}
                        style={{
                          color: STATUS_COLORS[claim.status],
                          borderColor: STATUS_COLORS[claim.status],
                        }}
                      >
                        {STATUS_LABELS[claim.status] || claim.status}
                      </span>
                    </td>

                    <td style={{ minWidth: 180 }}>
                      <div>Requested: {dateLabel(claim.created_at)}</div>

                      {claim.reviewed_at && (
                        <div className={panelStyles.subCell}>
                          Reviewed: {dateLabel(claim.reviewed_at)}
                        </div>
                      )}

                      {claim.processing_at && (
                        <div className={panelStyles.subCell}>
                          Processing: {dateLabel(claim.processing_at)}
                        </div>
                      )}

                      {claim.completed_at && (
                        <div className={panelStyles.subCell}>
                          Completed: {dateLabel(claim.completed_at)}
                        </div>
                      )}
                    </td>

                    <td>
                      <div
                        style={{
                          minWidth: 180,
                          maxWidth: 280,
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {claim.transaction_hash && (
                          <div>Transaction: {claim.transaction_hash}</div>
                        )}

                        {claim.rejection_reason && (
                          <div>Rejected: {claim.rejection_reason}</div>
                        )}

                        {claim.failure_reason && (
                          <div>Review: {claim.failure_reason}</div>
                        )}

                        {!claim.transaction_hash
                          && !claim.rejection_reason
                          && !claim.failure_reason
                          && '—'}
                      </div>
                    </td>

                    <td>
                      <div
                        style={{
                          display: 'flex',
                          gap: 8,
                          flexWrap: 'wrap',
                          minWidth: 170,
                        }}
                      >
                        {claim.status === 'pending' && (
                          <button
                            type="button"
                            className={panelStyles.actionBtn}
                            disabled={saving}
                            onClick={() => openAction(claim, 'approved')}
                          >
                            <FiCheckCircle /> Review & Approve
                          </button>
                        )}

                        {claim.status === 'approved' && (
                          <button
                            type="button"
                            className={panelStyles.actionBtn}
                            disabled={saving}
                            onClick={() => openAction(claim, 'processing')}
                          >
                            <FiSend /> Start Processing
                          </button>
                        )}

                        {['pending', 'approved'].includes(claim.status) && (
                          <button
                            type="button"
                            className={panelStyles.actionBtnReject}
                            disabled={saving}
                            onClick={() => openAction(claim, 'rejected')}
                          >
                            <FiXCircle /> Reject
                          </button>
                        )}

                        {claim.status === 'processing' && (
                          <button
                            type="button"
                            className={panelStyles.actionBtn}
                            disabled={saving}
                            onClick={() => openAction(claim, 'completed')}
                          >
                            <FiCheckCircle /> Record Transfer
                          </button>
                        )}

                        {claim.status === 'failed' && (
                          <span className={panelStyles.subCell}>
                            Tokens remain reserved. Transfer reconciliation
                            is required before further action.
                          </span>
                        )}

                        {['completed', 'rejected'].includes(claim.status) && (
                          <span className={panelStyles.subCell}>Closed</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            marginTop: 18,
          }}
        >
          <button
            type="button"
            className={panelStyles.actionBtn}
            disabled={loading || saving || offset === 0}
            onClick={() =>
              setOffset((value) => Math.max(0, value - PAGE_SIZE))
            }
          >
            Previous
          </button>

          <span>Page {Math.floor(offset / PAGE_SIZE) + 1}</span>

          <button
            type="button"
            className={panelStyles.actionBtn}
            disabled={loading || saving || Boolean(loadError) || !hasMore}
            onClick={() => setOffset((value) => value + PAGE_SIZE)}
          >
            Next
          </button>
        </div>
      </div>

      {selected && (
        <div className={styles.overlay} onClick={closeAction}>
          <div
            className={styles.modal}
            style={{ maxWidth: 620, width: '95%' }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="bct-action-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h3 id="bct-action-title">
                {ACTION_LABELS[selected.action]}
              </h3>

              <button
                type="button"
                aria-label="Close claim action"
                disabled={saving}
                onClick={closeAction}
              >
                <FiX />
              </button>
            </div>

            <div
              style={{
                lineHeight: 1.8,
                marginBottom: 20,
                overflowWrap: 'anywhere',
              }}
            >
              <div><strong>User:</strong> {selected.claim.full_name}</div>
              <div><strong>Email:</strong> {selected.claim.email}</div>
              <div><strong>Claim ID:</strong> {selected.claim.id}</div>
              <div><strong>Portal:</strong> {selected.claim.portal}</div>
              <div>
                <strong>Quantity:</strong>{' '}
                {quantity(selected.claim.token_amount)} BCT100
              </div>
              {Number(selected.claim.charge_tokens) > 0 && (
                <div>
                  <strong>Admin charge:</strong> {quantity(String(selected.claim.charge_admin_tokens || 0))} BCT100,{' '}
                  <strong>TDS:</strong> {quantity(String(selected.claim.charge_tds_tokens || 0))} BCT100 (total {quantity(String(selected.claim.charge_tokens))}).{' '}
                  <strong>Send to the user:</strong>{' '}
                  {quantity(String(Number(selected.claim.token_amount) - Number(selected.claim.charge_tokens)))} BCT100
                </div>
              )}
              <div>
                <strong>Receiving Wallet:</strong>{' '}
                {selected.claim.wallet_address}
              </div>
            </div>

            <form onSubmit={submitAction}>
              <fieldset
                disabled={saving}
                style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}
              >
                {selected.action === 'approved' && (
                  <>
                    <p className={styles.hint}>
                      Verify the receiving wallet address before approving.
                      If the address is incorrect, reject the claim so the
                      user can submit a corrected request.
                    </p>

                    <label
                      style={{
                        display: 'flex',
                        gap: 10,
                        alignItems: 'flex-start',
                        marginTop: 16,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={walletVerified}
                        onChange={(event) =>
                          setWalletVerified(event.target.checked)
                        }
                      />
                      <span>
                        I have verified the receiving wallet address.
                        Approval does not confirm a token transfer.
                      </span>
                    </label>
                  </>
                )}

                {selected.action === 'rejected' && (
                  <>
                    <label
                      className={styles.label}
                      htmlFor="bct-reject-reason"
                    >
                      Rejection Reason
                    </label>

                    <input
                      id="bct-reject-reason"
                      className={styles.input}
                      type="text"
                      maxLength={2000}
                      required
                      value={rejectionReason}
                      placeholder="Explain what the user needs to correct"
                      onChange={(event) =>
                        setRejectionReason(event.target.value)
                      }
                    />

                    <p className={styles.hint}>
                      The user will see this reason. Reserved tokens become
                      available again after rejection.
                    </p>
                  </>
                )}

                {selected.action === 'processing' && (
                  <>
                    <p className={styles.hint}>
                      This records that transfer processing has started.
                      It does not send tokens. Confirm this update succeeds
                      before making the transfer. Processing claims cannot
                      be rejected through this page.
                    </p>

                    <label
                      style={{
                        display: 'flex',
                        gap: 10,
                        alignItems: 'flex-start',
                        marginTop: 16,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={processingConfirmed}
                        onChange={(event) =>
                          setProcessingConfirmed(event.target.checked)
                        }
                      />
                      <span>
                        I have checked for existing transfers and will
                        coordinate this transfer to prevent duplicate sending.
                      </span>
                    </label>
                  </>
                )}

                {selected.action === 'completed' && (
                  <>
                    <label className={styles.label} htmlFor="bct-tx-hash">
                      Successful Transaction Hash
                    </label>

                    <input
                      id="bct-tx-hash"
                      className={styles.input}
                      value={transactionHash}
                      maxLength={255}
                      required
                      autoComplete="off"
                      spellCheck={false}
                      placeholder="Paste the confirmed transaction hash"
                      onChange={(event) =>
                        setTransactionHash(event.target.value)
                      }
                    />

                    <label
                      style={{
                        display: 'flex',
                        gap: 10,
                        alignItems: 'flex-start',
                        marginTop: 16,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={transferConfirmed}
                        onChange={(event) =>
                          setTransferConfirmed(event.target.checked)
                        }
                      />
                      <span>
                        I verified that the transfer succeeded and the exact
                        BCT100 quantity was sent to this receiving wallet.
                      </span>
                    </label>

                    <p className={styles.hint} style={{ marginTop: 14 }}>
                      This records your verified transfer. The system does
                      not independently verify the blockchain transaction.
                      Each claim requires a separate transaction hash.
                    </p>
                  </>
                )}

                <button
                  type="submit"
                  className={styles.saveBtn}
                  disabled={submitDisabled}
                  style={{ marginTop: 20 }}
                >
                  {saving ? 'Saving…' : ACTION_LABELS[selected.action]}
                </button>
              </fieldset>
            </form>
          </div>
        </div>
      )}
    </AdminShell>
  );
};

export default AdminBctTokenClaims;